import {
  $getSelection,
  $isRangeSelection,
  COMMAND_PRIORITY_HIGH,
  createCommand,
  LexicalCommand,
  LexicalEditor
} from 'lexical';
import { BehaviorSubject, Observable } from 'rxjs';
import { findExistingLinkUrl } from '../utils/editor-node.utils';
import { formatLink } from '../utils/editor-format.utils';
import { handlePotentialLinkClick } from '../utils/editor-events.utils';
import { mergeRegister } from '@lexical/utils';

export interface LinkDialogPlugin {
  dialogState$: Observable<LinkDialogState>;
  unregister: () => void;
}

export interface LinkDialogState {
  isOpen: boolean;
  url: string;
}

const DEFAULT_LINK_DIALOG_STATE: LinkDialogState = {
  isOpen: false,
  url: 'https://'
};

export const OPEN_LINK_DIALOG_COMMAND: LexicalCommand<void> = createCommand();
export const CLOSE_LINK_DIALOG_COMMAND: LexicalCommand<void> = createCommand();
export const APPLY_LINK_COMMAND: LexicalCommand<string | null> =
  createCommand();

/**
 * **Link Dialog Plugin**
 * Provides a dialog for creating and editing links in the editor.

 * AVAILABLE COMMANDS:
 * - `OPEN_LINK_DIALOG_COMMAND` - Opens the link dialog
 * - `CLOSE_LINK_DIALOG_COMMAND` - Closes the link dialog
 * - `APPLY_LINK_COMMAND` - Applies the link to the current selection
 *
 * @param editor - The Lexical editor instance
 * @returns {LinkDialogPlugin} - The link dialog plugin instance:
 * - `dialogState$` - Observable of the current dialog state
 * - `unregister` - Cleanup function to unregister the plugin
 */
export function registerLinkDialog(editor: LexicalEditor): LinkDialogPlugin {
  const dialogStateSubject = new BehaviorSubject<LinkDialogState>(
    DEFAULT_LINK_DIALOG_STATE
  );

  const unregisterCommandHandles = mergeRegister(
    editor.registerCommand(
      OPEN_LINK_DIALOG_COMMAND,
      () => {
        editor.update(() => {
          const selection = $getSelection();
          if (!$isRangeSelection(selection)) return false;

          const linkUrl = findExistingLinkUrl(selection);
          dialogStateSubject.next({
            isOpen: true,
            url: linkUrl
          });
          return false;
        });
        return true;
      },
      COMMAND_PRIORITY_HIGH
    ),
    editor.registerCommand(
      CLOSE_LINK_DIALOG_COMMAND,
      () => {
        dialogStateSubject.next({
          ...dialogStateSubject.value,
          isOpen: false
        });
        return true;
      },
      COMMAND_PRIORITY_HIGH
    ),
    editor.registerCommand(
      APPLY_LINK_COMMAND,
      url => {
        formatLink(editor, url);
        dialogStateSubject.next({
          isOpen: false,
          url: 'https://'
        });
        return true;
      },
      COMMAND_PRIORITY_HIGH
    )
  );

  const clickEventHandler = (e: MouseEvent) => {
    handlePotentialLinkClick(e.target as HTMLElement, editor, () => {
      editor.dispatchCommand(OPEN_LINK_DIALOG_COMMAND, undefined);
    });
  };

  const unregisterClickHandler = editor.registerRootListener(
    (rootElement, prevRootElement) => {
      prevRootElement?.removeEventListener('click', clickEventHandler);
      rootElement?.addEventListener('click', clickEventHandler);
    }
  );

  return {
    dialogState$: dialogStateSubject.asObservable(),
    unregister: () => {
      unregisterCommandHandles();
      unregisterClickHandler();
    }
  };
}
